// Shared look for every page: "Space" design — near-black, hairline borders, pill buttons,
// Geist type, soft blue accent, mint / coral for up / down. Kept calm so the data leads.
// The chart has the matching theme in Chart.js ("instrument", labelled "Space").
export const palette = {
  background: "#050608", // near-black
  panel: "#0d1015", // side rails, bars, panels
  glass: "rgba(10, 12, 16, 0.78)", // see-through panels over the background glow
  field: "#0a0c10", // inputs
  editor: "#0a0c10", // code editor and output console
  border: "rgba(255, 255, 255, 0.075)", // hairline dividers
  borderStrong: "rgba(255, 255, 255, 0.14)", // outlined (ghost) buttons
  text: "#eceef1",
  mutedText: "#9097a3",
  dimText: "#5a606b",
  accent: "#eceef1", // primary buttons: white pill
  accentText: "#050608",
  glow: "#6ea8ff", // active/selected outlines, links, focus
  accentBlue: "#6ea8ff",
  accentBlueSoft: "#a6c8ff",
  selected: "rgba(110, 168, 255, 0.12)", // selected list rows and toggles
  up: "#5fe0a8", // rising candles, buy, positive numbers
  down: "#ff7d8c", // falling candles, sell, negative numbers
  highlight: "#a6c8ff", // replay and first plotted line
  lavender: "#a497e6", // second plotted line
  danger: "#ff7d8c",
  dangerBg: "rgba(255, 125, 140, 0.1)",
  success: "#5fe0a8",
  successBg: "rgba(95, 224, 168, 0.1)",
};

export const fonts = {
  ui: '"Geist", "Archivo", system-ui, -apple-system, "Segoe UI", sans-serif',
  display: '"Geist", "Archivo", system-ui, sans-serif', // headings, labels, prices (weight 500, tight tracking)
  code: '"Geist Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};

/** Big headline numbers / words: white to soft blue, like the hero text. */
export const gradientText = {
  background: "linear-gradient(92deg, #ffffff 0%, #a6c8ff 45%, #6ea8ff 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};

/** Small mono caps label ("QUANTITATIVE TRADING / EST. 2023" style). */
export const eyebrow = {
  fontFamily: '"Geist Mono", ui-monospace, monospace',
  fontSize: "0.68rem",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#9097a3",
};

const radius = "10px";
const pill = "999px";

export const pageStyles = {
  fullPageCenter: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "transparent",
    color: palette.text,
    fontFamily: fonts.ui,
  },
  page: {
    minHeight: "100vh",
    background: "transparent", // the starfield on <body> shows through
    color: palette.text,
    display: "flex",
    flexDirection: "column",
    fontFamily: fonts.ui,
    fontVariantNumeric: "tabular-nums",
  },
  authWrap: {
    flex: 1,
    display: "grid",
    placeItems: "center",
    padding: "24px 16px",
  },
  card: {
    width: "100%",
    maxWidth: "380px",
    background: palette.glass,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: `1px solid ${palette.border}`,
    borderRadius: "16px",
    padding: "28px",
  },
  title: {
    margin: "0 0 6px",
    fontSize: "1.5rem",
    fontFamily: fonts.display,
    fontWeight: 500,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: "0 0 22px",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    color: palette.mutedText,
  },
  label: {
    display: "block",
    fontSize: "0.75rem",
    color: palette.mutedText,
    marginBottom: "6px",
    marginTop: "14px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "40px",
    padding: "9px 12px",
    borderRadius: radius,
    border: `1px solid ${palette.border}`,
    background: palette.field,
    color: palette.text,
    fontSize: "0.9rem",
  },
  button: {
    width: "100%",
    marginTop: "22px",
    minHeight: "44px",
    padding: "11px 18px",
    borderRadius: pill,
    border: "none",
    background: palette.accent,
    color: palette.accentText,
    fontSize: "0.9rem",
    fontWeight: 500,
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  linkRow: {
    marginTop: "16px",
    fontSize: "0.8rem",
    color: palette.mutedText,
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  link: {
    color: palette.text,
    textDecoration: "underline",
    textDecorationColor: palette.border,
    textUnderlineOffset: "3px",
    cursor: "pointer",
    background: "none",
    border: "none",
    font: "inherit",
    padding: 0,
  },
  errorBanner: {
    marginTop: "14px",
    padding: "9px 12px",
    borderRadius: radius,
    border: `1px solid ${palette.danger}`,
    background: palette.dangerBg,
    color: palette.danger,
    fontSize: "0.8rem",
  },
  successBanner: {
    marginTop: "14px",
    padding: "9px 12px",
    borderRadius: radius,
    border: `1px solid ${palette.success}`,
    background: palette.successBg,
    color: palette.success,
    fontSize: "0.8rem",
  },
  nav: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", // brand left, section tabs in the middle, account right
    alignItems: "center",
    gap: "0 16px",
    minHeight: "56px",
    padding: "0 16px",
    background: "rgba(5, 6, 8, 0.72)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderBottom: `1px solid ${palette.border}`,
  },
  navCenter: {
    display: "flex",
    gap: "3px",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
    minWidth: 0,
    maxWidth: "100%",
    overflowX: "auto",
    scrollbarWidth: "none",
    padding: "3px",
    border: `1px solid ${palette.border}`,
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.025)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.035)",
  },
  navBrand: {
    fontFamily: fonts.display,
    fontWeight: 600,
    fontSize: "0.82rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: palette.text,
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  navLinks: {
    display: "flex",
    gap: "4px",
    alignItems: "stretch",
    alignSelf: "stretch",
    justifySelf: "end",
    flexWrap: "nowrap", // one row; scrolls sideways on narrow phones instead of wrapping
    overflowX: "auto",
    maxWidth: "100%",
    scrollbarWidth: "none",
  },
  navLink: {
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    minHeight: "56px",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
    color: palette.mutedText,
    textDecoration: "none",
    background: "none",
    border: "none",
    font: "inherit",
    fontFamily: fonts.display,
    fontSize: "0.84rem",
    fontWeight: 550,
    letterSpacing: "0.015em",
    cursor: "pointer",
  },
  navLinkActive: {
    color: palette.text,
    fontWeight: 650,
    boxShadow: `inset 0 -1px 0 ${palette.text}`,
  },
};
